import type { SanityArticlePage } from '@lib/sanity';
import { TEAM_REVEAL_WORDPRESS_CATEGORY } from '@lib/constants';

/**
 * FFH-3569: what kind of article a pageview is, for Q8. PROVISIONAL until
 * editorial agree the list (FFH-3560); the app sends the same values, so a
 * change here is made in scoreline-app too. Every value lives in this one
 * table. Keys are Sanity article category slugs; team reveals are their own
 * document type, so they take their value from the document, not a category.
 */
export const ARTICLE_CONTENT_TYPES = {
  teamReveal: 'team_reveal',
  byCategorySlug: {
    'alternative-games': 'alternative_games',
    'fpl-captain': 'captaincy',
    'gameweek-tips': 'gameweek_tips',
    'how-to-play-fpl': 'guide',
    news: 'news',
    podcasts: 'podcast',
    'pre-season': 'pre_season',
    stats: 'analysis',
    'sun-dream-team': 'alternative_games',
    'ultimate-guides': 'guide'
  },
  // An uncategorised article, or a category the table does not know yet.
  unmapped: 'other'
} as const;

type ArticleContentTypes = typeof ARTICLE_CONTENT_TYPES;

export type ArticleContentType =
  | ArticleContentTypes['teamReveal']
  | ArticleContentTypes['byCategorySlug'][keyof ArticleContentTypes['byCategorySlug']]
  | ArticleContentTypes['unmapped'];

/** The article properties an article's $pageview carries (FFH-3569). */
export type ArticlePageviewProperties = {
  content_type: ArticleContentType;
  // The byline as published; null for an article with no author.
  author: string | null;
};

type SanityArticlePageviewSource = Pick<
  SanityArticlePage,
  '_type' | 'category' | 'author'
>;

type WordPressCategoryNode = { categoryId: number; name: string };

type WordPressArticlePageviewSource = {
  author?: { node?: { name?: string | null } | null } | null;
  categories?: { nodes?: WordPressCategoryNode[] | null } | null;
};

const SANITY_ARTICLE_ROUTES: ReadonlySet<string> = new Set([
  '/articles/[slug]',
  '/team-reveals/[author]/[slug]'
]);
const WORDPRESS_POST_ROUTE = '/[slug]';

const NON_ALPHANUMERIC_RUN_PATTERN = /[^a-z0-9]+/g;
const EDGE_HYPHEN_PATTERN = /^-+|-+$/g;

const contentTypeForCategorySlug = (
  slug: string | null | undefined
): ArticleContentType | undefined =>
  slug &&
  Object.prototype.hasOwnProperty.call(
    ARTICLE_CONTENT_TYPES.byCategorySlug,
    slug
  )
    ? ARTICLE_CONTENT_TYPES.byCategorySlug[
        slug as keyof ArticleContentTypes['byCategorySlug']
      ]
    : undefined;

// WordPress returns category names, not slugs. The Sanity categories were
// set up from the WordPress ones, so the name slugged the way Sanity slugs a
// title ("Gameweek tips" to gameweek-tips) finds the same row.
const toCategorySlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(NON_ALPHANUMERIC_RUN_PATTERN, '-')
    .replace(EDGE_HYPHEN_PATTERN, '');

const toSanityArticleProperties = (
  page: SanityArticlePageviewSource
): ArticlePageviewProperties => ({
  content_type:
    page._type === 'teamReveal'
      ? ARTICLE_CONTENT_TYPES.teamReveal
      : contentTypeForCategorySlug(page.category?.slug) ??
        ARTICLE_CONTENT_TYPES.unmapped,
  author: page.author?.name ?? null
});

const toWordPressPostProperties = (
  post: WordPressArticlePageviewSource
): ArticlePageviewProperties => {
  const categories = post.categories?.nodes ?? [];
  const isTeamReveal = categories.some(
    (category) => category.categoryId === TEAM_REVEAL_WORDPRESS_CATEGORY
  );
  const categoryContentType = categories
    .map((category) =>
      contentTypeForCategorySlug(toCategorySlug(category.name))
    )
    .find((contentType) => contentType !== undefined);

  return {
    content_type: isTeamReveal
      ? ARTICLE_CONTENT_TYPES.teamReveal
      : categoryContentType ?? ARTICLE_CONTENT_TYPES.unmapped,
    author: post.author?.node?.name ?? null
  };
};

/**
 * The article properties for the page being viewed, read from its page
 * props, or null on any page that is not an article: Sanity articles and
 * team reveals, and the legacy WordPress posts at /<slug>.
 */
export const toArticlePageviewProperties = (
  routePattern: string,
  pageProps: {
    page?: SanityArticlePageviewSource | null;
    post?: WordPressArticlePageviewSource | null;
  } | null
): ArticlePageviewProperties | null => {
  if (SANITY_ARTICLE_ROUTES.has(routePattern) && pageProps?.page) {
    return toSanityArticleProperties(pageProps.page);
  }
  if (routePattern === WORDPRESS_POST_ROUTE && pageProps?.post) {
    return toWordPressPostProperties(pageProps.post);
  }
  return null;
};
